import { router } from "@inertiajs/react";
import { Fragment, useEffect, useState } from "react";
import Header from "../Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 2,
    }).format(Number(value || 0));

const payoutStatusClass = (status) => {
    switch (Number(status)) {
        case 1:
            return "bg-green-100 text-green-700";
        case 2:
            return "bg-red-100 text-red-700";
        default:
            return "bg-amber-100 text-amber-700";
    }
};

export default function Payouts({ payouts, filters = {} }) {
    const [notification, setNotification] = useState(null);
    const [busyKey, setBusyKey] = useState("");
    const [openPayoutIds, setOpenPayoutIds] = useState([]);
    const [search, setSearch] = useState(filters.search ?? "");
    const [week, setWeek] = useState(filters.week ?? "");
    const [perPage, setPerPage] = useState(String(filters.per_page ?? 10));
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [paidForm, setPaidForm] = useState({
        reference_no: "",
        notes: "",
    });

    const payoutRows = Array.isArray(payouts?.data) ? payouts.data : [];

    useEffect(() => {
        setSearch(filters.search ?? "");
        setWeek(filters.week ?? "");
        setPerPage(String(filters.per_page ?? 10));
    }, [filters.search, filters.week, filters.per_page]);

    const reloadPage = () => window.location.reload();
    const visitWithFilters = (nextFilters = {}) => {
        router.get(
            "/panel/commission-payouts",
            {
                search,
                week,
                per_page: perPage,
                ...nextFilters,
            },
            {
                preserveState: true,
                replace: true,
            }
        );
    };

    const togglePayoutDetails = (payoutId) => {
        setOpenPayoutIds((prev) =>
            prev.includes(payoutId)
                ? prev.filter((id) => id !== payoutId)
                : [...prev, payoutId]
        );
    };

    const applyFilters = (event) => {
        event.preventDefault();
        setOpenPayoutIds([]);
        visitWithFilters({ page: 1 });
    };

    const resetFilters = () => {
        setSearch("");
        setWeek("");
        setPerPage("10");
        setOpenPayoutIds([]);
        router.get(
            "/panel/commission-payouts",
            { page: 1, per_page: 10 },
            { preserveState: true, replace: true }
        );
    };

    const openPaidModal = (payout) => {
        setSelectedPayout(payout);
        setPaidForm({
            reference_no: payout.reference_no ?? "",
            notes: payout.notes ?? "",
        });
        setNotification(null);
    };

    const closePaidModal = () => {
        if (
            selectedPayout &&
            busyKey === `paid-${selectedPayout.payout_id}`
        ) {
            return;
        }

        setSelectedPayout(null);
        setPaidForm({
            reference_no: "",
            notes: "",
        });
    };

    const setPaidField = (field, value) => {
        setPaidForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const markPaid = async () => {
        if (!selectedPayout) {
            return;
        }

        setBusyKey(`paid-${selectedPayout.payout_id}`);
        setNotification(null);

        try {
            const response = await window.axios.post(
                `/panel/reseller-commissions/payouts/${selectedPayout.payout_id}/mark-paid`,
                {
                    reference_no: paidForm.reference_no,
                    notes: paidForm.notes,
                }
            );
            setNotification({
                type: "success",
                message: response?.data?.message ?? "Payout marked as paid successfully.",
            });
            setSelectedPayout(null);
            setPaidForm({
                reference_no: "",
                notes: "",
            });
            reloadPage();
        } catch (error) {
            setNotification({
                type: "error",
                message:
                    error?.response?.data?.message ??
                    "Failed to mark payout as paid.",
            });
        } finally {
            setBusyKey("");
        }
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div>
                            <h1 className="text-lg md:text-xl font-semibold text-foreground">Payout Batches</h1>
                            <p className="text-sm text-muted-foreground-1">Weekly payout batch monitoring and reconciliation.</p>
                        </div>

                        {notification && (
                            <div className={`rounded-lg border px-4 py-3 text-sm ${
                                notification.type === "success"
                                    ? "border-green-200 bg-green-50 text-green-700"
                                    : "border-red-200 bg-red-50 text-red-700"
                            }`}>
                                {notification.message}
                            </div>
                        )}

                        <form onSubmit={applyFilters} className="rounded-xl border border-card-line bg-card p-4 shadow-2xs">
                            <div className="grid gap-4 md:grid-cols-4">
                                <div className="md:col-span-2">
                                    <label className="mb-1 block text-sm font-medium text-foreground">Search reseller</label>
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search by reseller name or phone"
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-foreground">Week</label>
                                    <input
                                        type="week"
                                        value={week}
                                        onChange={(event) => setWeek(event.target.value)}
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-foreground">Rows</label>
                                    <select
                                        value={perPage}
                                        onChange={(event) => setPerPage(event.target.value)}
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    >
                                        {[10, 25, 50].map((value) => (
                                            <option key={value} value={value}>
                                                {value} rows
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="submit"
                                    className="inline-flex items-center rounded-lg border border-primary-line bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
                                >
                                    Apply Filters
                                </button>
                                <button
                                    type="button"
                                    onClick={resetFilters}
                                    className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover"
                                >
                                    Reset
                                </button>
                            </div>
                        </form>

                        <div className="bg-card border border-card-line rounded-xl shadow-2xs overflow-hidden">
                            <div className="px-5 py-3 border-b border-card-header">
                                <h2 className="font-semibold text-foreground">Payout Batch List</h2>
                            </div>
                            <div className="p-5 overflow-x-auto">
                                <table className="min-w-full divide-y divide-table-line">
                                    <thead>
                                        <tr>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Payout</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Reseller</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Commissions</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Total</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Status</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-table-line">
                                        {payoutRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="py-6 px-4 text-center text-sm text-muted-foreground-2">
                                                    No payout batches yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            payoutRows.map((payout) => {
                                                const isOpen = openPayoutIds.includes(payout.payout_id);
                                                const payoutCommissions = Array.isArray(payout.commissions) ? payout.commissions : [];

                                                return (
                                                    <Fragment key={payout.payout_id}>
                                                        <tr>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => togglePayoutDetails(payout.payout_id)}
                                                                    className="flex items-start gap-3 text-left"
                                                                >
                                                                    <span className="mt-0.5 text-xs text-muted-foreground-1">
                                                                        {isOpen ? "▾" : "▸"}
                                                                    </span>
                                                                    <span>
                                                                        <div>{payout.payout_code}</div>
                                                                        <div className="text-xs text-muted-foreground-1">
                                                                            Ref: {payout.reference_no || "-"}
                                                                        </div>
                                                                    </span>
                                                                </button>
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                <div>{payout.member?.user?.name ?? "-"}</div>
                                                                <div className="text-xs text-muted-foreground-1">{payout.member?.mem_code ?? "-"}</div>
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                {payoutCommissions.length}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">{formatCurrency(payout.payout_total_amount)}</td>
                                                            <td className="py-3 px-4">
                                                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${payoutStatusClass(payout.payout_status)}`}>
                                                                    {payout.payout_status_label}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => togglePayoutDetails(payout.payout_id)}
                                                                        className="inline-flex items-center rounded-lg border border-layer-line bg-white px-3 py-2 text-sm font-medium text-foreground hover:bg-slate-50"
                                                                    >
                                                                        {isOpen ? "Hide Details" : "Show Details"}
                                                                    </button>
                                                                    {Number(payout.payout_status) === 0 ? (
                                                                        <button
                                                                            type="button"
                                                                            disabled={busyKey === `paid-${payout.payout_id}`}
                                                                            onClick={() => openPaidModal(payout)}
                                                                            className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover disabled:opacity-50"
                                                                        >
                                                                            {busyKey === `paid-${payout.payout_id}` ? "Saving..." : "Mark Paid"}
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-sm text-muted-foreground-1">Complete</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {isOpen && (
                                                            <tr className="bg-slate-50/70">
                                                                <td colSpan={6} className="px-4 py-4">
                                                                    <div className="rounded-lg border border-table-line bg-white p-4">
                                                                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                                                            <div>
                                                                                <h3 className="text-sm font-semibold text-foreground">
                                                                                    Commission Ledger
                                                                                </h3>
                                                                                <p className="text-xs text-muted-foreground-1">
                                                                                    Detailed commissions included in this payout batch.
                                                                                </p>
                                                                            </div>
                                                                            <div className="text-xs text-muted-foreground-1">
                                                                                {payoutCommissions.length} commission item{payoutCommissions.length === 1 ? "" : "s"}
                                                                            </div>
                                                                        </div>
                                                                        <div className="overflow-x-auto">
                                                                            <table className="min-w-full divide-y divide-table-line">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th className="py-2.5 px-3 text-start text-xs font-normal text-muted-foreground-1">Order</th>
                                                                                        <th className="py-2.5 px-3 text-start text-xs font-normal text-muted-foreground-1">Product</th>
                                                                                        <th className="py-2.5 px-3 text-start text-xs font-normal text-muted-foreground-1">Type</th>
                                                                                        <th className="py-2.5 px-3 text-start text-xs font-normal text-muted-foreground-1">Rate</th>
                                                                                        <th className="py-2.5 px-3 text-start text-xs font-normal text-muted-foreground-1">Amount</th>
                                                                                        <th className="py-2.5 px-3 text-start text-xs font-normal text-muted-foreground-1">Status</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="divide-y divide-table-line">
                                                                                    {payoutCommissions.length === 0 ? (
                                                                                        <tr>
                                                                                            <td colSpan={6} className="px-3 py-4 text-center text-sm text-muted-foreground-2">
                                                                                                No commission ledger details found.
                                                                                            </td>
                                                                                        </tr>
                                                                                    ) : (
                                                                                        payoutCommissions.map((commission) => (
                                                                                            <tr key={commission.soc_id}>
                                                                                                <td className="px-3 py-3 text-sm text-foreground">
                                                                                                    <div>{commission.sales_order?.so_code ?? "-"}</div>
                                                                                                    <div className="text-xs text-muted-foreground-1">
                                                                                                        {commission.reseller_code_snapshot ?? "-"}
                                                                                                    </div>
                                                                                                </td>
                                                                                                <td className="px-3 py-3 text-sm text-foreground">
                                                                                                    <div>{commission.variant?.product?.prod_name ?? "-"}</div>
                                                                                                    <div className="text-xs text-muted-foreground-1">
                                                                                                        {commission.variant?.prod_var_name ?? "-"}
                                                                                                    </div>
                                                                                                </td>
                                                                                                <td className="px-3 py-3 text-sm text-foreground">
                                                                                                    {commission.commission_type_label ?? "-"}
                                                                                                </td>
                                                                                                <td className="px-3 py-3 text-sm text-foreground">
                                                                                                    {Number(commission.commission_rate ?? 0)}
                                                                                                </td>
                                                                                                <td className="px-3 py-3 text-sm text-foreground">
                                                                                                    {formatCurrency(commission.commission_amount)}
                                                                                                </td>
                                                                                                <td className="px-3 py-3 text-sm text-foreground">
                                                                                                    {commission.commission_status_label ?? "-"}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="border-t border-card-header px-5 py-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="text-sm text-muted-foreground-1">
                                        Showing {payouts?.from ?? 0}-{payouts?.to ?? 0} of {payouts?.total ?? 0}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            disabled={!payouts?.prev_page_url}
                                            onClick={() => visitWithFilters({ page: (payouts?.current_page ?? 1) - 1 })}
                                            className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-sm text-muted-foreground-1">
                                            Page {payouts?.current_page ?? 1} of {payouts?.last_page ?? 1}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={!payouts?.next_page_url}
                                            onClick={() => visitWithFilters({ page: (payouts?.current_page ?? 1) + 1 })}
                                            className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            {selectedPayout && (
                <div className="fixed inset-0 z-60 flex items-center justify-center px-4">
                    <div
                        className="absolute inset-0 bg-slate-900/50"
                        onClick={closePaidModal}
                    />
                    <div className="relative z-10 w-full max-w-2xl rounded-2xl border border-card-line bg-card shadow-xl">
                        <div className="flex items-start justify-between gap-4 border-b border-card-header px-6 py-4">
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    Confirm Payout Payment
                                </h2>
                                <p className="mt-1 text-sm text-muted-foreground-1">
                                    Review the reseller payout details before marking {selectedPayout.payout_code} as paid.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closePaidModal}
                                className="rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover"
                            >
                                Close
                            </button>
                        </div>

                        <div className="space-y-6 px-6 py-5">
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="rounded-xl border border-card-line bg-slate-50 p-4">
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground-1">
                                        Reseller
                                    </div>
                                    <div className="mt-2 text-sm font-semibold text-foreground">
                                        {selectedPayout.member?.user?.name ?? "-"}
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground-1">
                                        Member Code: {selectedPayout.member?.mem_code ?? "-"}
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground-1">
                                        Phone: {selectedPayout.member?.mem_phone ?? "-"}
                                    </div>
                                </div>

                                <div className="rounded-xl border border-card-line bg-slate-50 p-4">
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground-1">
                                        Payout Summary
                                    </div>
                                    <div className="mt-2 text-sm font-semibold text-foreground">
                                        {selectedPayout.payout_code}
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground-1">
                                        Total: {formatCurrency(selectedPayout.payout_total_amount)}
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground-1">
                                        Items: {Array.isArray(selectedPayout.commissions) ? selectedPayout.commissions.length : 0}
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-xl border border-card-line p-4">
                                <h3 className="text-sm font-semibold text-foreground">
                                    Bank Account Information
                                </h3>
                                <div className="mt-4 grid gap-4 md:grid-cols-3">
                                    <div>
                                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground-1">
                                            Bank Name
                                        </div>
                                        <div className="mt-2 text-sm text-foreground">
                                            {selectedPayout.member?.mem_bank_name ?? "-"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground-1">
                                            Account Name
                                        </div>
                                        <div className="mt-2 text-sm text-foreground">
                                            {selectedPayout.member?.mem_bank_account_name ?? "-"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground-1">
                                            Account Number
                                        </div>
                                        <div className="mt-2 break-all text-sm text-foreground">
                                            {selectedPayout.member?.mem_bank_account_number ?? "-"}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-foreground">
                                        Reference No
                                    </label>
                                    <input
                                        type="text"
                                        value={paidForm.reference_no}
                                        onChange={(event) => setPaidField("reference_no", event.target.value)}
                                        placeholder="Optional payment reference number"
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-foreground">
                                        Notes
                                    </label>
                                    <textarea
                                        value={paidForm.notes}
                                        onChange={(event) => setPaidField("notes", event.target.value)}
                                        rows={4}
                                        placeholder="Optional admin notes for this payout"
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col-reverse gap-2 border-t border-card-header px-6 py-4 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={closePaidModal}
                                disabled={busyKey === `paid-${selectedPayout.payout_id}`}
                                className="inline-flex items-center justify-center rounded-lg border border-layer-line bg-layer px-4 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={markPaid}
                                disabled={busyKey === `paid-${selectedPayout.payout_id}`}
                                className="inline-flex items-center justify-center rounded-lg border border-primary-line bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                            >
                                {busyKey === `paid-${selectedPayout.payout_id}` ? "Saving..." : "Confirm Paid"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
